import React, { useCallback, useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  TouchableOpacity,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import Icons from "@/assets/icons";
import { Input } from "@/components/Input";
import Select from "@/components/Select";
import BottomSheet from "@/components/BottomSheet";
import { US_STATES } from "@/constants/data";
import { Address } from "@/types/unclaimed";
import UnclaimedServices from "@/services/unclaimedServices";
import { showError } from "@/utils/toast";
import { scale } from "@/utils/appStyleSheet";

type AddressErrors = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
};

const validateZipCode = (zip: string): string | undefined => {
  const trimmed = zip.trim();
  if (!trimmed) {
    return "Zip code is required";
  }
  if (!/^\d{5}(-\d{4})?$/.test(trimmed)) {
    return "Enter a valid 5-digit zip code";
  }
  return undefined;
};

const validateStreet = (street: string): string | undefined => {
  const trimmed = street.trim();
  if (!trimmed) {
    return "Street address is required";
  }
  if (trimmed.length < 5) {
    return "Enter a valid street address";
  }
  return undefined;
};

const validateCity = (city: string): string | undefined => {
  const trimmed = city.trim();
  if (!trimmed) {
    return "City is required";
  }
  if (trimmed.length < 2) {
    return "Enter a valid city name";
  }
  return undefined;
};

const validateState = (state: string): string | undefined => {
  if (!state) {
    return "State is required";
  }
  return undefined;
};

type NameInfo = {
  first: string;
  last: string;
};

const UnclaimedMoneyAddressScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [names, setNames] = useState<NameInfo[]>([]);
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Form state for bottom sheet
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [state, setState] = useState("");
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});

  useEffect(() => {
    const fetchData = async () => {
      // Fetch existing unclaimed search data
      const response = await UnclaimedServices.getCurrentUnclaimedSearch();
      if (response.success && response.data) {
        setAddresses(response.data.addresses || []);
        if (response.data.names && response.data.names.length > 0) {
          setNames(response.data.names.map((n: { first: string; last: string }) => ({
            first: n.first,
            last: n.last,
          })));
        }
      }
    };
    fetchData();
  }, []);

  const resetForm = useCallback(() => {
    setStreet("");
    setCity("");
    setZipCode("");
    setState("");
    setAddressErrors({});
    setEditingIndex(null);
  }, []);

  const handleClose = useCallback(() => {
    setIsBottomSheetVisible(false);
    resetForm();
  }, [resetForm]);

  const validateAddress = useCallback((): boolean => {
    const errors: AddressErrors = {};

    const streetError = validateStreet(street);
    if (streetError) errors.street = streetError;

    const cityError = validateCity(city);
    if (cityError) errors.city = cityError;

    const stateError = validateState(state);
    if (stateError) errors.state = stateError;

    const zipError = validateZipCode(zipCode);
    if (zipError) errors.zip = zipError;

    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  }, [street, city, state, zipCode]);

  const handleFieldChange = useCallback(
    (field: keyof AddressErrors, value: string, setter: (_v: string) => void) => {
      setter(value);
      setAddressErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const handleAddOrUpdateAddress = useCallback(() => {
    if (validateAddress()) {
      const newAddress: Address = {
        street: street.trim(),
        city: city.trim(),
        state,
        zip: zipCode.trim(),
      };

      if (editingIndex !== null) {
        // Update existing address
        setAddresses((prev) =>
          prev.map((addr, index) => (index === editingIndex ? newAddress : addr))
        );
      } else {
        // Add new address
        setAddresses((prev) => [...prev, newAddress]);
      }

      setIsBottomSheetVisible(false);
      resetForm();
    }
  }, [validateAddress, street, city, state, zipCode, editingIndex, resetForm]);

  const handleEditAddress = useCallback((index: number) => {
    const address = addresses[index];
    setStreet(address.street);
    setCity(address.city);
    setState(address.state);
    setZipCode(address.zip);
    setEditingIndex(index);
    setIsBottomSheetVisible(true);
  }, [addresses]);

  const handleAddAddress = useCallback(() => {
    resetForm();
    setIsBottomSheetVisible(true);
  }, [resetForm]);

  const handleContinue = async () => {
    if (addresses.length === 0) {
      showError("Please add at least one address", "Missing Address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await UnclaimedServices.submitUnclaimedSearch({
        names,
        addresses,
      });
      setIsLoading(false);

      if (response.success) {
        router.push("/signup/enable-refund-helper");
      } else {
        // If addresses are already being searched, still proceed to next screen
        if (response.message?.includes("already being searched")) {
          router.push("/signup/enable-refund-helper");
        } else {
          showError(response.message || "Failed to submit unclaimed search", "Submission Failed");
        }
      }
    } catch (error) {
      setIsLoading(false);
      showError("Failed to submit unclaimed search", "Submission Failed");
    }
  };

  const formatAddress = (address: Address) => {
    return `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View style={styles.progressFill} />
          </View>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.headerSection}>
            <Text style={styles.sectionLabel}>Unclaimed money</Text>
            <Text style={styles.title}>Unclaimed money search</Text>
            <Text style={styles.subtitle}>
              Find hidden checks tied to your name and mailing address. The
              average unclaimed check is over $50.
            </Text>
          </View>

          {/* Address List */}
          {addresses.map((item, index) => (
            <View key={`${item.street}-${index}`}>
              <View style={styles.addressCard}>
                <Text style={styles.addressText}>{formatAddress(item)}</Text>
                <TouchableOpacity
                  onPress={() => handleEditAddress(index)}
                  style={styles.addressActionButton}
                >
                  <Icons.EditLine width={24} height={24} />
                </TouchableOpacity>
              </View>
              {index < addresses.length - 1 && <View style={styles.addressSeparator} />}
            </View>
          ))}

          {/* Add Address */}
          <TouchableOpacity style={styles.addAddressRow} onPress={handleAddAddress}>
            <View style={styles.addIconContainer}>
              <Ionicons name="add" size={16} color="#161D14" />
            </View>
            <Text style={styles.addAddressText}>Add another address to track</Text>
          </TouchableOpacity>
        </View>

        {/* Spacer */}
        <View style={styles.spacer} />

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          <View style={styles.buttonContainer}>
            <ActionButton
              title="Continue"
              onPress={handleContinue}
              disabled={isLoading || addresses.length === 0}
            />
          </View>
          <Text style={styles.disclaimer}>*All numbers are estimated</Text>
        </View>

        {/* Bottom Sheet for Adding/Editing Address */}
        <BottomSheet
          isVisible={isBottomSheetVisible}
          onClose={handleClose}
          title={editingIndex !== null ? "Edit address" : "Add address"}
          scrollable
        >
          <View style={styles.bottomSheetContent}>
            <Input
              placeholder="Street address"
              label="Street address"
              value={street}
              error={addressErrors.street}
              onChangeText={(text) => handleFieldChange("street", text, setStreet)}
              autoCapitalize="words"
            />
            <Input
              placeholder="City"
              label="City"
              value={city}
              error={addressErrors.city}
              onChangeText={(text) => handleFieldChange("city", text, setCity)}
              autoCapitalize="words"
            />
            <View style={styles.row}>
              <Input
                placeholder="Zip code"
                label="Zip code"
                value={zipCode}
                error={addressErrors.zip}
                onChangeText={(text) => handleFieldChange("zip", text, setZipCode)}
                containerStyle={styles.flexGrow}
                keyboardType="number-pad"
                maxLength={10}
              />
              <View style={styles.flexGrow}>
                <Select
                  placeholder="State"
                  label="State"
                  value={state}
                  options={US_STATES.map((s) => ({
                    label: s.split(",")[0],
                    value: s.split(",")[0],
                  }))}
                  onSelect={(value) => {
                    setState(String(value.value));
                    setAddressErrors((prev) => ({ ...prev, state: undefined }));
                  }}
                  containerStyle={styles.flexGrow}
                />
                {addressErrors.state && (
                  <Text style={styles.selectError}>{addressErrors.state}</Text>
                )}
              </View>
            </View>
            <ActionButton
              title={editingIndex !== null ? "Update address" : "Add address"}
              onPress={handleAddOrUpdateAddress}
            />
          </View>
        </BottomSheet>
      </SafeAreaView>
    </View>
  );
};

export default UnclaimedMoneyAddressScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  progressContainer: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 16,
  },
  progressBar: {
    height: 6,
    backgroundColor: "#E1EFE4",
    borderRadius: 100,
  },
  progressFill: {
    width: 16,
    height: 6,
    backgroundColor: "rgba(22, 29, 20, 0.5)",
    borderRadius: 100,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  headerSection: {
    gap: 12,
    marginBottom: 24,
  },
  sectionLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 16,
    lineHeight: 22,
    color: "#0F9129",
    letterSpacing: -0.32,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    letterSpacing: -0.64,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: 0.16,
  },
  addressCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#F3F3F3",
    borderRadius: 8,
    padding: 16,
  },
  addressText: {
    flex: 1,
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
  },
  addressActionButton: {
    padding: 4,
  },
  addressSeparator: {
    height: 8,
  },
  addAddressRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
  },
  addIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#EBEBEB",
    justifyContent: "center",
    alignItems: "center",
  },
  addAddressText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
  },
  spacer: {
    flex: 1,
  },
  bottomSection: {
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F3F3",
    paddingTop: 12,
  },
  buttonContainer: {
    marginBottom: 12,
  },
  disclaimer: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
  },
  bottomSheetContent: {
    gap: 0,
  },
  row: {
    flexDirection: "row",
    gap: 16,
  },
  flexGrow: {
    flexGrow: 1,
  },
  selectError: {
    color: "#FF0000",
    fontSize: 12,
    marginTop: -12,
    marginBottom: 16,
  },
});
